/**
 * useCreditAnimation Hook
 *
 * Animates credit counter changes with a shrink → drop-in pattern:
 *   1. Current counter shrinks (scale 0.88, 200ms)
 *   2. New counter drops in from above (translateY, 400ms)
 *
 * For multi-credit decreases (e.g., incorrect guess costs 2),
 * steps are queued and played sequentially with a brief pause between.
 *
 * Animation is chained off the prism rotation's onTransitionEnd via
 * signalPrismComplete(), with a 100ms delay after the prism finishes.
 */

import { useState, useCallback, useEffect, useRef } from 'react';

export const CREDIT_SHRINK_SCALE = 0.88;
export const CREDIT_SHRINK_DURATION = 200;
export const CREDIT_DROP_DURATION = 400;
const STEP_PAUSE = 90;
const POST_PRISM_DELAY = 100;

// Module-level trigger: ClueCell signals when prism rotation completes
let pendingStart: (() => void) | null = null;

/** Call from ClueCell's prism onTransitionEnd to kick off credit animation */
export function signalPrismComplete() {
  if (pendingStart) {
    const fn = pendingStart;
    pendingStart = null;
    setTimeout(fn, POST_PRISM_DELAY);
  }
}

// Module-level: credit animation completion callbacks
let creditAnimCompleteCallback: (() => void) | null = null;

/** Register a one-shot callback that fires when credit animation queue empties */
export function registerCreditAnimComplete(cb: (() => void) | null) {
  creditAnimCompleteCallback = cb;
}

function fireCreditAnimComplete() {
  if (creditAnimCompleteCallback) {
    const cb = creditAnimCompleteCallback;
    creditAnimCompleteCallback = null;
    cb();
  }
}

export type CreditAnimPhase = 'idle' | 'shrinking' | 'pre-drop' | 'dropping';

export function useCreditAnimation(credits: number) {
  const [displayedCredit, setDisplayedCredit] = useState(credits);
  const [incomingCredit, setIncomingCredit] = useState(credits);
  const [phase, setPhase] = useState<CreditAnimPhase>('idle');
  const prevCreditsRef = useRef(credits);
  const queueRef = useRef<number[]>([]);
  const animatingRef = useRef(false);
  const delayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Detect credit changes
  useEffect(() => {
    const prev = prevCreditsRef.current;
    prevCreditsRef.current = credits;

    if (credits > prev) {
      // Credits increased (game reset) — snap immediately
      if (delayTimerRef.current) clearTimeout(delayTimerRef.current);
      pendingStart = null;
      setDisplayedCredit(credits);
      setIncomingCredit(credits);
      setPhase('idle');
      queueRef.current = [];
      animatingRef.current = false;
      return;
    }

    if (credits >= prev) return;

    // Credits decreased — build step queue (one per credit)
    const steps: number[] = [];
    for (let i = prev - 1; i >= credits; i--) {
      steps.push(i);
    }

    if (!animatingRef.current) {
      // Register start callback — will fire when ClueCell calls signalPrismComplete()
      animatingRef.current = true;
      queueRef.current = steps.slice(1);
      pendingStart = () => {
        setIncomingCredit(steps[0]);
        setPhase('shrinking');
      };
    } else {
      // Already animating (or waiting) — append to queue
      queueRef.current.push(...steps);
    }
  }, [credits]);

  // Shrink → pre-drop (driven by onShrinkEnd from CreditCounter's onTransitionEnd)
  // Pre-drop → dropping (double-rAF for CSS transition mount)
  useEffect(() => {
    if (phase === 'pre-drop') {
      // Double rAF so incoming element mounts at translateY(-size) before transition starts
      let cancelled = false;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!cancelled) setPhase('dropping');
        });
      });
      return () => { cancelled = true; };
    }
  }, [phase]);

  // Called when shrink CSS transition ends (from CreditCounter's onTransitionEnd)
  const onShrinkEnd = useCallback(() => {
    if (phase === 'shrinking') setPhase('pre-drop');
  }, [phase]);

  // Called when drop CSS transition ends
  const onDropEnd = useCallback(() => {
    setDisplayedCredit(incomingCredit);

    if (queueRef.current.length > 0) {
      const next = queueRef.current.shift()!;
      setIncomingCredit(next);
      // Brief idle so counter renders at full scale before next shrink
      setPhase('idle');
      setTimeout(() => setPhase('shrinking'), STEP_PAUSE);
    } else {
      animatingRef.current = false;
      setPhase('idle');
      fireCreditAnimComplete();
    }
  }, [incomingCredit]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (delayTimerRef.current) clearTimeout(delayTimerRef.current);
      pendingStart = null;
    };
  }, []);

  return { phase, displayedCredit, incomingCredit, onShrinkEnd, onDropEnd };
}
